import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Deposit,
  DepositWithLines,
  ListDepositsQuery,
  RecordDepositInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export const depositsKey = (companyId: string | null, filters: Partial<ListDepositsQuery> = {}) =>
  ['deposits', companyId ?? 'none', filters.bankAccountId ?? 'all', filters.status ?? 'any'] as const;

export function useDeposits(companyId: string | null, filters: Partial<ListDepositsQuery> = {}) {
  return useQuery({
    queryKey: depositsKey(companyId, filters),
    enabled: companyId !== null,
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.bankAccountId) params.set('bankAccountId', filters.bankAccountId);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      return api
        .get<{ deposits: Deposit[] }>(`/companies/${companyId}/deposits${qs ? '?' + qs : ''}`)
        .then((r) => r.deposits);
    },
  });
}

export function useDeposit(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['deposit', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api
        .get<{ deposit: DepositWithLines }>(`/companies/${companyId}/deposits/${id}`)
        .then((r) => r.deposit),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  qc.invalidateQueries({ queryKey: ['deposits', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['deposit', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', companyId ?? 'none'] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useRecordDeposit(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordDepositInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ deposit: DepositWithLines }>(`/companies/${companyId}/deposits`, input)
        .then((r) => r.deposit);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useUpdateDeposit(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordDepositInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ deposit: DepositWithLines }>(`/companies/${companyId}/deposits/${id}`, input)
        .then((r) => r.deposit);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useVoidDeposit(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ deposit: Deposit }>(`/companies/${companyId}/deposits/${id}/void`, { reason });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useSetDepositCleared(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cleared }: { id: string; cleared: boolean }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ deposit: Deposit }>(`/companies/${companyId}/deposits/${id}/clear`, { cleared });
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
